// The in-memory twin of CLOSED-LOOPS (contracts/api/commitments.ts): requests asked of the caller
// and by them, and one action route for every move. The rules are the server's, cut down
// (services/api/src/domain/commitments/store.ts `rolesOf` / `movesOf`, without the overseer
// roles): accept → working, decline → rejected, submit → in_verification (mechanical: completed),
// verify pass → completed, verify fail → working with the feedback, fail → failed with a report
// and a decision owed by the requester, choose → completed with the option. A move not open to the
// caller is a 409. Starts empty; tests seed with `seed`.
// `fake-commitments-contract.test.ts` holds it to the contracts.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem } from "./fake";
import { uid } from "./fake-data";

type View = OperationOutput<"commitments.get">;
type Party = View["requester"];
type Option = NonNullable<View["options"]>[number];
type Move = View["moves"][number];
type Role = View["my_roles"][number];
type LogEntry = NonNullable<View["log"]>[number];
type Stored = Omit<View, "my_roles" | "moves" | "log"> & { company_id: string; log: LogEntry[] };

/** A test's commitment: who asks and who owes (membership ids), the rest as the view's fields. */
export type FakeCommitmentSeed = { requester: string; owner: string } & Partial<
  Omit<View, "my_roles" | "moves" | "log" | "requester" | "owner" | "id">
>;

const HOUR = 3_600_000;
const TERMINAL = new Set(["completed", "failed", "rejected", "canceled", "expired"]);
const KEYS = ["A", "B", "C", "D"];
const validation = (detail: string) =>
  problem(422, "validation_failed", "Validation failed", detail);
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);

export function createCommitmentsFake(deps: {
  now: () => Date;
  /** A member of the company as a party (a person or a DE); null when unknown. */
  partyOf: (companyId: string, membershipId: string) => Omit<Party, "membership_id"> | null;
}): {
  handlers: Partial<Record<RouteId, Handler>>;
  seed: (companyId: string, seed: FakeCommitmentSeed) => View;
} {
  let seq = 0;
  const rows: Stored[] = [];
  const stamp = () => deps.now().toISOString();
  const later = (hours: number) => new Date(deps.now().getTime() + hours * HOUR).toISOString();
  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const party = (co: string, id: string): Party | null => {
    const known = deps.partyOf(co, id);
    return known ? { ...known, membership_id: id } : null;
  };
  const verifierOf = (c: Stored) =>
    c.verify_method === "requester"
      ? c.requester.membership_id
      : c.verify_method === "mechanical"
        ? null
        : c.verifier_membership_id;

  function rolesOf(c: Stored, me: string | null): Role[] {
    if (!me) return [];
    const out: Role[] = [];
    if (c.requester.membership_id === me) out.push("requester");
    if (c.owner.membership_id === me) out.push("owner");
    if (verifierOf(c) === me && (c.owner.membership_id !== me || c.owner.kind === "helper"))
      out.push("verifier");
    if (
      c.kind === "decision" &&
      (c.owner.membership_id === me || c.escalated_to_membership_id === me)
    )
      out.push("decider");
    if (c.escalated_to_membership_id === me) out.push("escalated");
    return out;
  }

  function movesOf(c: Stored, roles: Role[]): Move[] {
    if (TERMINAL.has(c.state)) return [];
    const has = (r: Role) => roles.includes(r);
    const out = new Set<Move>();
    if (c.kind === "decision") {
      if (has("decider")) out.add("choose").add("forward");
    } else if (has("owner")) {
      if (c.state === "submitted") out.add("accept").add("reject");
      if (["working", "input_required", "auth_required"].includes(c.state))
        out.add("progress").add("fail").add("forward");
      if (c.state === "working") out.add("submit");
    }
    if (has("verifier") && c.state === "in_verification") out.add("verify");
    if (has("requester")) out.add("cancel").add("extend");
    if (has("escalated")) out.add("extend").add("forward").add("cancel");
    return [...out];
  }

  const isMine = (c: Stored, me: string) =>
    c.owner.membership_id === me ||
    c.waiting_on?.membership_id === me ||
    c.escalated_to_membership_id === me ||
    c.verifier_membership_id === me;

  function view(c: Stored, me: string | null, withLog = false): View {
    const { company_id: _co, log, ...rest } = c;
    const my_roles = rolesOf(c, me);
    return { ...rest, my_roles, moves: movesOf(c, my_roles), ...(withLog ? { log } : {}) };
  }

  function record(c: Stored, by: string | null, action: string, from: string, note?: string) {
    const at = stamp();
    c.log.push({
      at,
      action,
      actor_kind: by ? "person" : "system",
      actor_membership_id: by,
      from,
      to: c.state,
      rung: null,
      told: [],
      note: note ?? null,
    });
    if (from !== c.state) c.state_at = at;
    c.updated_at = at;
  }

  function make(co: string, requester: Party, owner: Party, s: Partial<Stored>): Stored {
    const at = stamp();
    const due = s.due_at ?? later(24);
    return {
      company_id: co,
      id: uid(0x7c3e, ++seq),
      kind: "request",
      request_type: "request",
      state: "submitted",
      state_reason: null,
      state_at: at,
      intent: "",
      expected_outcome: "",
      done_when: [],
      requester,
      owner,
      accountable_membership_id: null,
      waiting_on: null,
      waiting_since: null,
      due_at: due,
      accept_by: s.accept_by ?? due,
      verify_method: "requester",
      verifier_membership_id: null,
      verify_by: null,
      expires_at: new Date(Date.parse(due) + 7 * 24 * HOUR).toISOString(),
      parent_id: null,
      context_id: null,
      subject: null,
      result: null,
      evidence: [],
      feedback: null,
      attempts: 0,
      max_attempts: 3,
      replans: 0,
      max_replans: 1,
      loops: 0,
      blocker_category: null,
      blocker_detail: null,
      cause: null,
      failure: null,
      failure_text: null,
      options: null,
      default_option: null,
      chosen_option: null,
      choice_note: null,
      rung: 0,
      next_rung_at: null,
      escalated_to_membership_id: null,
      created_at: at,
      updated_at: at,
      log: [],
      ...s,
    };
  }

  type OptionInput = {
    label: string;
    detail?: string | null;
    action?: Option["action"];
    recommended?: boolean;
  };
  function optionsOf(input: OptionInput[] | undefined): Option[] | Response {
    if (!input) return [];
    if (input.length < 2 || input.length > 4) return validation("options: 2 to 4 options");
    const recommended = input.findIndex((o) => o.recommended);
    return input.map((o, i) => ({
      key: KEYS[i] as string,
      label: o.label,
      detail: o.detail ?? null,
      action: o.action ?? "other",
      recommended: i === (recommended < 0 ? 0 : recommended),
    }));
  }

  const find = (p: Principal, id: string | undefined) => {
    const me = membershipOf(p);
    const c = rows.find((r) => r.company_id === p.company.id && r.id === id);
    if (!c || !me || (rolesOf(c, me).length === 0 && !isMine(c, me))) return null;
    return { c, me };
  };

  const handlers: Partial<Record<RouteId, Handler>> = {
    "commitments.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const me = membershipOf(p);
      if (!me) return json({ data: [] });
      const side = url.searchParams.get("side") ?? "all";
      const open = url.searchParams.get("open") !== "false";
      const mine = rows.filter((c) => {
        if (c.company_id !== p.company.id) return false;
        const of = isMine(c, me);
        const by = c.requester.membership_id === me;
        const who = side === "of_me" ? of : side === "by_me" ? by : of || by;
        return who && TERMINAL.has(c.state) !== open;
      });
      const data = open
        ? mine.sort((a, b) => a.due_at.localeCompare(b.due_at))
        : mine.sort((a, b) => b.state_at.localeCompare(a.state_at)).slice(0, 50);
      return json({ data: data.map((c) => view(c, me)) });
    },
    "commitments.get": ({ principal: p, params }) => {
      if (!p) return notFound();
      const found = find(p, params.id);
      return found ? json(view(found.c, found.me, true)) : notFound();
    },
    "commitments.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const me = membershipOf(p);
      const b = (body ?? {}) as {
        owner_membership_id?: string;
        intent?: string;
        expected_outcome?: string;
        done_when?: string[];
        request_type?: string;
        due_at?: string;
        accept_by?: string;
        verify?: View["verify_method"];
        verifier_membership_id?: string;
        parent_id?: string;
        options?: OptionInput[];
        default_option?: string;
      };
      const requester = me ? party(p.company.id, me) : null;
      if (!requester) return notFound();
      const owner = b.owner_membership_id ? party(p.company.id, b.owner_membership_id) : null;
      if (!owner) return validation("owner_membership_id: not a member of this company");
      if (!b.intent?.trim()) return validation("intent: say what and why");
      if (!b.expected_outcome?.trim())
        return validation("expected_outcome: say what done looks like");
      const options = optionsOf(b.options);
      if (options instanceof Response) return options;
      const c = make(p.company.id, requester, owner, {
        kind: options.length ? "decision" : "request",
        intent: b.intent.trim(),
        expected_outcome: b.expected_outcome.trim(),
        done_when: b.done_when ?? [],
        request_type: b.request_type ?? "request",
        ...(b.due_at ? { due_at: b.due_at, accept_by: b.accept_by ?? b.due_at } : {}),
        verify_method: b.verify ?? "requester",
        verifier_membership_id: b.verifier_membership_id ?? null,
        parent_id: b.parent_id ?? null,
        options: options.length ? options : null,
        default_option: b.default_option ?? null,
      });
      rows.push(c);
      record(c, me, "created", "submitted");
      return json(view(c, me), 201);
    },
    "commitments.act": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const found = find(p, params.id);
      if (!found) return notFound();
      const { c, me } = found;
      const b = (body ?? {}) as {
        action?: Move;
        reason?: string;
        note?: string;
        result?: string;
        evidence?: View["evidence"];
        pass?: boolean;
        feedback?: string;
        option?: string;
        to_membership_id?: string;
        due_at?: string;
        attempt?: boolean;
        replan?: boolean;
        failure?: {
          cause: NonNullable<View["cause"]>;
          category?: View["blocker_category"];
          what_happened: string;
          tried: string[];
          options: OptionInput[];
          recommendation: string;
          question?: string;
          default_option?: string;
          decide_by?: string;
        };
      };
      const action = b.action;
      if (!action || !movesOf(c, rolesOf(c, me)).includes(action))
        return conflict(`"${action ?? ""}" is not open to you now (${c.state})`);
      const from = c.state;
      switch (action) {
        case "accept":
          c.state = "working";
          break;
        case "reject":
          c.state = "rejected";
          c.state_reason = b.reason ?? null;
          break;
        case "progress":
          if (b.attempt) c.attempts += 1;
          if (b.replan) c.replans += 1;
          break;
        case "submit":
          c.result = b.result ?? null;
          c.evidence = b.evidence ?? [];
          c.feedback = null;
          if (c.verify_method === "mechanical") c.state = "completed";
          else {
            c.state = "in_verification";
            c.verify_by = later(24);
          }
          break;
        case "verify":
          if (b.pass === undefined) return validation("pass: say whether it passes");
          if (b.pass) c.state = "completed";
          else {
            if (!b.feedback?.trim()) return validation("feedback: say what misses");
            c.state = "working";
            c.feedback = b.feedback.trim();
            c.loops += 1;
          }
          c.verify_by = null;
          break;
        case "fail": {
          const f = b.failure;
          if (!f) return validation("failure: a failure needs its report");
          const options = optionsOf(f.options);
          if (options instanceof Response) return options;
          const decideBy = f.decide_by ?? later(24);
          const text = [
            f.what_happened,
            f.tried.length ? `Tried: ${f.tried.join("; ")}.` : "",
            f.recommendation,
          ]
            .filter(Boolean)
            .join(" ");
          Object.assign(c, {
            state: "failed",
            cause: f.cause,
            failure: {
              cause: f.cause,
              category: f.category ?? null,
              what_happened: f.what_happened,
              tried: f.tried,
              partial: [],
              options,
              recommendation: f.recommendation,
              question: f.question ?? "What should happen next?",
              decide_by: decideBy,
              default_option: f.default_option ?? null,
              logs: [],
            },
            failure_text: text,
            options,
            default_option: f.default_option ?? null,
          });
          // the decision it makes owed: the requester picks an option by the deadline
          const decision = make(c.company_id, c.owner, c.requester, {
            kind: "decision",
            request_type: "decision",
            intent: f.question ?? `Decide what happens with: ${c.intent}`,
            expected_outcome: "An option chosen",
            due_at: decideBy,
            parent_id: c.id,
            failure: c.failure,
            failure_text: text,
            options,
            default_option: f.default_option ?? null,
            cause: f.cause,
          });
          rows.push(decision);
          record(decision, me, "created", "submitted");
          break;
        }
        case "cancel":
          c.state = "canceled";
          c.state_reason = b.reason ?? null;
          break;
        case "choose": {
          const picked = c.options?.find((o) => o.key === b.option);
          if (!picked) return validation("option: not one of this decision's options");
          c.state = "completed";
          c.chosen_option = picked.key;
          c.choice_note = b.note ?? null;
          break;
        }
        case "forward": {
          const to = b.to_membership_id ? party(c.company_id, b.to_membership_id) : null;
          if (!to) return validation("to_membership_id: not a member of this company");
          c.owner = to;
          break;
        }
        case "extend":
          if (!b.due_at || Date.parse(b.due_at) <= deps.now().getTime())
            return validation("due_at: a later due time");
          c.due_at = b.due_at;
          break;
      }
      record(c, me, action, from, b.note ?? b.reason ?? b.feedback);
      return json(view(c, me));
    },
  };

  function seed(companyId: string, s: FakeCommitmentSeed): View {
    const { requester: r, owner: o, ...rest } = s;
    const requester = party(companyId, r);
    const owner = party(companyId, o);
    if (!requester || !owner) throw new Error("seedCommitment: unknown requester or owner");
    const c = make(companyId, requester, owner, rest);
    rows.push(c);
    return view(c, null);
  }

  return { handlers, seed };
}
